<template>
	<div>
		<div class="filter-header">
			<Input
				v-model="searchText"
				type="text"
				:placeholder="$t('contacts_userpicker_search_placeholder')"
				autoFocus
				:icon="SearchIcon"
				:iconProps="{ type: 'regular' }"
				ref="userFilter"
				class="search-input"
			/>
			<Dropdown
				v-if="!isThread"
				:options="dropdownOptions"
				:icon="FilterIcon"
				v-model="selectedFilter"
				class="filter-role"
			>
				<template #selected="{ option }">
					<span v-t="'contacts_userpicker_filter_title'" />
					<span class="selected-label" v-text="option.label" />
				</template>
				<template v-slot="{ option }">
					<div class="role-title" v-text="option ? option.label : ''" />
					<div class="role-desc" v-text="option ? option.desc : ''" />
				</template>
			</Dropdown>
		</div>

		<Table
			class="members-table"
			:columns="columns"
			:rows="users"
			keyField="id"
			ref="table"
			:height="tableHeight"
			:loadingOverlay="isLoading"
			:loadingMore="isLoadingMore"
			:emptyText="$t('common_loader_noresults_title')"
			@load-more="loadMore"
			@row-click="showUserModal($event.id, bubbleId)"
			loadMoreTrigger="200px"
			clickable
			bordered
			infinite
		>
			<template #cell-id="{ data }">
				<div class="persona">
					<ProfilePic :key="data.id" :user="data" size="40px" />
					<UserName class="name" :user="data" />
				</div>
			</template>
			<template #cell-membershiprole="{ value }">
				<div class="user-role">
					<Tag v-if="value === 'owner' && !isThread" primary>
						{{ $t('model_role_owner') }}
					</Tag>
				</div>
			</template>
			<template #column-id>
				{{
					$t(
						isThread
							? 'admin_groups_members_label'
							: 'admin_groups_group_members_title'
					)
				}}
				<span class="members-count" v-if="membersCount" v-text="membersCount" />
			</template>
			<template #column-membershiprole>
				<Button
					v-if="canAddMembers"
					small
					primary
					@click="showAddMembers = true"
				>
					{{ $t('admin_groups_add_members_action') }}
				</Button>
			</template>
		</Table>

		<AddMembersModal
			v-if="showAddMembers"
			@add="addUsersToGroup"
			@cancel="showAddMembers = false"
			:group="group"
			:startingCount="group.memberships_count || 0"
			:maxLimit="maxLimit"
		/>
	</div>
</template>

<script>
import { markRaw } from 'vue'
import { getChat, getOrganization, setUsers } from '@/store'
import {
	UserName,
	Input,
	Dropdown,
	Button,
	Table,
	Tag,
	ProfilePic,
} from '@/components/common'
import { fetchBubble, addUsersToChat } from '@/api/chat'
import { get } from '@/api/rest'
import { SearchIcon, FilterIcon } from '@/icons'
import AddMembersModal from '@/components/CreateGroup/AddMembersModal'
import debounce from 'debounce'
import { chatCan, ADD_MEMBER } from '@/helpers/permissions'
import { showUserModal } from '@/helpers/utils'

export default {
	name: 'GroupPeople',
	components: {
		UserName,
		Input,
		Dropdown,
		Button,
		Table,
		Tag,
		ProfilePic,
		AddMembersModal,
	},
	props: { bubbleId: [Number, String] },
	data() {
		const org = getOrganization()
		return {
			searchText: '',
			selectedFilter: 'all',
			SearchIcon: markRaw(SearchIcon),
			FilterIcon: markRaw(FilterIcon),
			users: [],
			group: {},
			isLoading: true,
			isLoadingMore: false,
			showAddMembers: false,
			tableHeight: '300px',
			maxLimit: org.bubble_membership_cap,
			columns: [
				{ field: 'id' },
				{ field: 'membershiprole', width: '175px', align: 'right' },
			],
		}
	},
	created() {
		this.searchForUserDebounced = debounce(this.searchForUser.bind(this), 500)
	},
	mounted() {
		this.fetchBubble()
		this.searchForUser()

		this.$onEventBus({
			userRoleUpdated: () => {
				this.onMembershipChange()
			},
			removedUserFromProfileCard: () => {
				this.onMembershipChange()
			},
		})

		const onResizeDebounced = debounce(this.onContainerResize, 50)
		this.container = document.querySelector('.group-settings-content')
		this.observer = new ResizeObserver(onResizeDebounced)
		this.observer.observe(this.container)
	},
	beforeUnmount() {
		this.container = null
		this.observer?.disconnect()
	},
	computed: {
		query() {
			return this.searchText.trim()
		},
		membersCount() {
			if (this.query || this.selectedFilter !== 'all') return null
			if (this.isLoading) return '(--)'
			return `(${this.group.memberships_count || 0})`
		},
		dropdownOptions() {
			return [
				{
					id: 'all',
					label: this.$t('groupsettings_permissions_everyone'),
					desc: this.$t('groupsettings_membership_filter_everyone_description'),
				},
				{
					id: 'owner',
					label: this.$t('groupsettings_permissions_owners'),
					desc: this.$t('groupsettings_membership_filter_owners_description'),
				},
			]
		},
		isThread() {
			return !!getChat(this.group?.id)?.is_thread
		},
		canAddMembers() {
			if (this.isThread) return false
			const chat = getChat(this.group?.id)
			return chatCan(chat, ADD_MEMBER)
		},
	},
	watch: {
		searchText() {
			this.searchForUserDebounced()
		},
		selectedFilter() {
			this.searchForUserDebounced()
		},
	},
	methods: {
		showUserModal,
		async onMembershipChange() {
			this.isLoading = true
			await this.searchForUserDebounced()
			await this.fetchBubble()
		},
		async fetchBubble() {
			// TODO: swap this over to fetchChat
			const resp = await fetchBubble({
				bubble_id: this.bubbleId,
				memberships_count: true,
			})
			this.group = resp.bubble
			return this.group
		},
		// Normalize membership users through the store before rendering. The REST
		// memberships payload can carry a stale legacy `profilepicurl` that
		// disagrees with the authoritative `profile_pic_url`; normalizeUser (via
		// setUsers) resolves that so ProfilePic shows the correct avatar/initials.
		membershipRows(memberships) {
			const normalized = setUsers(memberships.map((m) => m.user)) || []
			const byId = new Map(normalized.map((u) => [u.id, u]))
			return memberships.map((m) => ({
				...(byId.get(m.user?.id) || m.user),
				membershiprole: m.role,
			}))
		},

		async searchForUser() {
			this.isLoading = true

			if (this.abortCtrl) {
				this.abortCtrl.abort()
			}
			const abortCtrl = new AbortController()
			this.abortCtrl = abortCtrl
			try {
				const params = {
					include: 'user',
					sort: 'user.firstname,user.lastname',
				}
				if (this.query) {
					params['filter[search]'] = this.query
				}
				if (this.selectedFilter === 'owner') {
					params['filter[role]'] = 'owner'
				}

				const resp = await get(`/chats/${this.bubbleId}/memberships`, params, {
					signal: abortCtrl.signal,
				})
				if (this.abortCtrl !== abortCtrl) return null
				this.apiResponse = resp
				this.users = this.membershipRows(resp.data)
				this.abortCtrl = null
			} catch (e) {
				if (abortCtrl.signal.aborted) return null
				console.error(e)
				this.$alert.error(e)
			}
			this.isLoading = false
		},

		async loadMore() {
			if (!this.apiResponse?.next) return
			this.isLoadingMore = true
			const prevResponse = this.apiResponse
			try {
				const resp = await prevResponse.next()
				if (this.apiResponse !== prevResponse) return
				this.apiResponse = resp
				this.users = this.users.concat(this.membershipRows(resp.data))
			} catch (e) {
				console.error(e)
			} finally {
				this.isLoadingMore = false
			}
		},

		async addUsersToGroup(users) {
			try {
				this.isAddingMembers = true
				const userIds = users.map((u) => u.id)
				await addUsersToChat(this.group.id, userIds)
				await this.onMembershipChange()
				this.showAddMembers = false
			} catch (e) {
				this.$alert.error(e)
			}
			this.isAddingMembers = false
		},

		onContainerResize() {
			let newHeight = this.container.offsetHeight - 210
			if (newHeight < 200) {
				newHeight = 200
			}
			this.tableHeight = `${newHeight}px`
		},
	},
}
</script>
<style lang="scss" scoped>
@import 'src/sass/variables';

.search-input {
	background: white;
}

.filter-header {
	width: 100%;
	height: 60px;
	@include flex-spread(16px);
	padding: 0 16px;
	background: var(--neutral-100);
	border: 1px solid var(--neutral-200);
	border-radius: 6px;
	.filter-role {
		width: 210px;
		flex-shrink: 0;
		:deep(.dropdown-option) {
			@include flex-column;
			.role-title {
				color: var(--neutral-800);
			}
			.role-desc {
				color: var(--neutral-400);
				font-size: 12px;
			}
		}
		:deep(.dropdown-container) {
			.dropdown-selected {
				background: white;
				.selected-label {
					margin-left: 4px;
					color: var(--blue-300);
				}
			}
		}
	}
}

.members-table {
	margin-top: 24px;
	:deep(.table-row) {
		&.table-header {
			.table-cell {
				height: 50px;
				&.right {
					flex-shrink: 0;
				}
			}
		}
	}
	.members-count {
		margin-left: 4px;
	}
}

.persona {
	@include flex(12px);
	flex-shrink: 0;
	.name {
		@include ellipsis;
		max-width: 400px;
	}
}
</style>
<style lang="scss">
@import 'src/sass/variables';

@include overrideScheme('dark') {
	.members-table.table-container .table-body {
		background: var(--neutral-100);
	}
	.search-input.pronto-input {
		background: var(--neutral-200);
	}
	.filter-role .dropdown-selected {
		background: var(--neutral-200) !important;
	}
}
</style>
